/**
 * AllAlertsPage — Alert marketplace with 6 types, geo-filtering, create/confirm/resolve.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/App";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";
import GeoFilterBar from "@/components/GeoFilterBar";
import {
  Loader2, Search, MapPin, Globe, Shield, AlertTriangle,
  CloudRain, Lock, HeartPulse, Car, Skull, Plus, CheckCircle,
  XCircle, Clock, Tag,
} from "lucide-react";

const API = (() => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "http://localhost:8001/api";
  return `https://api.${host}/api`;
})();

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ALERT_TYPES_META = {
  imminent_threat: { name: "Imminent Threat", color: "from-red-600 to-red-800", bg: "bg-red-600/20", text: "text-red-400", border: "border-red-600/40", icon: Skull },
  natural_disaster: { name: "Natural Disaster", color: "from-amber-500 to-orange-600", bg: "bg-amber-600/20", text: "text-amber-400", border: "border-amber-600/40", icon: CloudRain },
  scam_fraud: { name: "Scam & Fraud", color: "from-purple-600 to-violet-700", bg: "bg-purple-600/20", text: "text-purple-400", border: "border-purple-600/40", icon: Shield },
  accident_injury: { name: "Accident & Injury", color: "from-orange-500 to-red-500", bg: "bg-orange-600/20", text: "text-orange-400", border: "border-orange-600/40", icon: Car },
  security_breach: { name: "Security Breach", color: "from-blue-600 to-cyan-600", bg: "bg-blue-600/20", text: "text-blue-400", border: "border-blue-600/40", icon: Lock },
  public_health: { name: "Public Health", color: "from-emerald-500 to-teal-600", bg: "bg-emerald-600/20", text: "text-emerald-400", border: "border-emerald-600/40", icon: HeartPulse },
};

const SEVERITY_COLORS = {
  low: "bg-zinc-600/30 text-zinc-300",
  medium: "bg-amber-600/30 text-amber-300",
  high: "bg-orange-600/30 text-orange-300",
  critical: "bg-red-600/30 text-red-300",
};

export default function AllAlertsPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [alertType, setAlertType] = useState("all");
  const [category, setCategory] = useState("all");
  const [severity, setSeverity] = useState("all");
  const [country, setCountry] = useState("all");
  const [province, setProvince] = useState("all");
  const [radius, setRadius] = useState("");
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", alert_type: "imminent_threat", category: "Active Shooter", severity: "medium", location: "", country: "ZA", province: "", city: "", source: "" });

  useEffect(() => {
    fetchAlerts();
    fetchMeta();
  }, []);

  const fetchMeta = async () => {
    try {
      const [typesRes, catsRes] = await Promise.all([
        axios.get(`${API}/all-alerts/types`, { headers: getHeaders() }),
        axios.get(`${API}/all-alerts/categories`, { headers: getHeaders() }),
      ]);
      setTypes(typesRes.data.types || {});
      setCategories(catsRes.data.categories || []);
    } catch {}
  };

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (alertType !== "all") params.set("alert_type", alertType);
      if (category !== "all") params.set("category", category);
      if (severity !== "all") params.set("severity", severity);
      if (country !== "all") params.set("country", country);
      if (province !== "all") params.set("province", province);
      if (radius) { params.set("radius_km", radius); }
      const res = await axios.get(`${API}/all-alerts/list?${params}`, { headers: getHeaders() });
      setAlerts(res.data.items || []);
    } catch { setAlerts([]); }
    setLoading(false);
  };

  useEffect(() => { fetchAlerts(); }, [alertType, category, severity, country, province, radius]);

  const handleCreate = async () => {
    if (!form.title || !form.description) { toast.error("Title and description required"); return; }
    try {
      const res = await axios.post(`${API}/all-alerts/create`, form, { headers: getHeaders() });
      toast.success("Alert reported!");
      setShowCreate(false);
      setForm({ title: "", description: "", alert_type: "imminent_threat", category: "Active Shooter", severity: "medium", location: "", country: "ZA", province: "", city: "", source: "" });
      fetchAlerts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create"); }
  };

  const handleConfirm = async (alertId) => {
    try {
      await axios.post(`${API}/all-alerts/${alertId}/confirm`, {}, { headers: getHeaders() });
      toast.success("Alert confirmed");
      fetchAlerts();
    } catch { toast.error("Failed to confirm"); }
  };

  const handleResolve = async (alertId) => {
    try {
      await axios.post(`${API}/all-alerts/${alertId}/resolve`, {}, { headers: getHeaders() });
      toast.success("Alert resolved");
      fetchAlerts();
    } catch { toast.error("Failed to resolve"); }
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = Math.floor((new Date() - d) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-app text-app">
      <TopNav />
      <div className="max-w-7xl mx-auto px-3 py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold">All Alerts</h1>
              <p className="text-xs text-app-secondary">Real-time community incident reporting</p>
            </div>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-red-600 hover:bg-red-500 text-white text-xs px-3 py-1.5 h-auto rounded-lg">
                <Plus className="w-3.5 h-3.5 mr-1" /> Report Alert
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-app-card border-white/10 text-app max-h-[80vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Report an Alert</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input placeholder="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="bg-surface-item border-input text-app" />
                <Textarea placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-surface-item border-input text-app" rows={3} />
                <select value={form.alert_type} onChange={e => { setForm({...form, alert_type: e.target.value, category: "" }); }}
                  className="w-full bg-surface-item border border-input rounded px-2 py-1.5 text-xs text-app">
                  {Object.entries(ALERT_TYPES_META).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
                </select>
                <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                  className="w-full bg-surface-item border border-input rounded px-2 py-1.5 text-xs text-app">
                  <option value="">Select category...</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={form.severity} onChange={e => setForm({...form, severity: e.target.value})}
                  className="w-full bg-surface-item border border-input rounded px-2 py-1.5 text-xs text-app">
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
                </select>
                <Input placeholder="City" value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="bg-surface-item border-input text-app" />
                <Input placeholder="Source (optional)" value={form.source} onChange={e => setForm({...form, source: e.target.value})} className="bg-surface-item border-input text-app" />
                <Button onClick={handleCreate} className="w-full bg-red-600 hover:bg-red-500 text-white">Submit Alert</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Alert Type Filter Pills */}
        <div className="flex gap-1 mb-2 overflow-x-auto scrollbar-dark">
          <button onClick={() => setAlertType("all")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${alertType === "all" ? "bg-red-600/30 text-red-300 font-semibold" : "bg-surface-item text-app-secondary"}`}>All</button>
          {Object.entries(ALERT_TYPES_META).map(([k, v]) => (
            <button key={k} onClick={() => setAlertType(k)}
              className={`text-xs px-3 py-1 rounded-full whitespace-nowrap font-medium ${alertType === k ? `${v.bg} ${v.text} font-semibold` : "bg-surface-item text-app-secondary hover:bg-white/5"}`}>
              {v.name}
            </button>
          ))}
        </div>

        {/* Geo + Category filters */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <GeoFilterBar country={country} onCountryChange={setCountry} region={province} onRegionChange={setProvince} radius={radius} onRadiusChange={setRadius} />
        </div>
        <div className="flex gap-1 mb-2 overflow-x-auto scrollbar-dark">
          <button onClick={() => setCategory("all")} className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${category === "all" ? "bg-red-600/30 text-red-300 font-semibold" : "bg-surface-item text-app-secondary"}`}>All</button>
          {categories.filter((_, i) => i < 15).map(c => (
            <button key={c} onClick={() => setCategory(c)} className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${category === c ? "bg-red-600/30 text-red-300" : "bg-surface-item text-app-secondary"}`}>{c}</button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-secondary" />
          <input type="text" value={search} onChange={e => { setSearch(e.target.value); }}
            placeholder="Search alerts..." className="w-full bg-surface-item border border-input rounded-lg pl-8 pr-3 py-1.5 text-xs text-app placeholder:text-app-secondary focus:outline-none focus:border-red-500" />
        </div>

        {/* Alerts List */}
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-app-secondary" /></div>
        ) : alerts.length === 0 ? (
          <Card className="bg-app-card border-white/10 p-8 text-center">
            <AlertTriangle className="w-10 h-10 text-app-secondary mx-auto mb-2" />
            <p className="text-sm text-app-secondary">No alerts found</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {alerts.filter(a => !search || a.title.toLowerCase().includes(search.toLowerCase()) || a.description?.toLowerCase().includes(search.toLowerCase())).map(alert => {
              const meta = ALERT_TYPES_META[alert.alert_type] || ALERT_TYPES_META.imminent_threat;
              const Icon = meta.icon;
              return (
                <Card key={alert.id} className={`bg-app-card border ${meta.border} overflow-hidden`}>
                  <div className={`h-1 w-full bg-gradient-to-r ${meta.color}`} />
                  <div className="p-3">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg ${meta.bg} flex items-center justify-center shrink-0`}>
                        <Icon className={`w-5 h-5 ${meta.text}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="text-sm font-semibold text-app">{alert.title}</h3>
                            <p className="text-xs text-app-secondary mt-0.5">{alert.description}</p>
                          </div>
                          <Badge className={`${SEVERITY_COLORS[alert.severity] || "bg-zinc-600/30 text-zinc-300"} text-[9px] px-1.5 py-0 shrink-0`}>{alert.severity}</Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[10px] text-app-secondary">
                          <span className={`${meta.text} font-medium`}>{meta.name}</span>
                          <span>{alert.category}</span>
                          {alert.province && <span><MapPin className="w-3 h-3 inline mr-0.5" />{alert.city || alert.province}</span>}
                          {alert.distance_km && <span>{alert.distance_km}km away</span>}
                          <span><Clock className="w-3 h-3 inline mr-0.5" />{formatTime(alert.created_at)}</span>
                          <span>{alert.confirmed_count || 0} confirmed</span>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <button onClick={() => handleConfirm(alert.id)} className="text-[10px] bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 px-2 py-0.5 rounded flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Confirm
                          </button>
                          <button onClick={() => handleResolve(alert.id)} className="text-[10px] bg-zinc-600/20 text-zinc-400 hover:bg-zinc-600/30 px-2 py-0.5 rounded flex items-center gap-1">
                            <XCircle className="w-3 h-3" /> Resolve
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
